// Bank Statements: every statement Fleetly holds, and every month it does not.
//
// WHY THIS PAGE EXISTS, WHICH IS ALSO WHY IT LEADS WITH THE GAPS.
//
// Migration 0113 built the bank-evidence half of driver cash on 30 August 2026.
// On 18 September it had never held a row: no statement upload, no statement
// line, no deposit match, against 315 declared deposits waiting to be checked
// against them. Nothing was broken. There was simply no door, because the only
// way in was a CSV column mapper on one tab of the Driver Cash page, and the
// statement the bank sends is a PDF.
//
// So the first thing on this page is not what is held. It is what is missing,
// month by month, with the money waiting in each gap beside it, because on the
// day it shipped the honest answer was "everything" and a screen that opened on
// an empty list of uploads would have said nothing at all.
//
// Every other screen that says NO_STATEMENT_LOADED is pointing at a row here.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Landmark, ShieldAlert, Upload, AlertCircle, FileText, Trash2, CheckCircle2 } from 'lucide-react'
import {
  useGetStatementAccess,
  useGetStatementLibrary,
  useGetStatementGaps,
  useGetBankAccounts,
  useUploadBankStatementPdf,
  useRemoveStatementUpload,
  useGetStatementDocumentUrl,
} from '../hooks/backend/banking'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Button } from '../lib/shadcn/button'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../lib/shadcn/tabs'
import { Chip } from '../components/interviews/Chip'
import { toast } from '../lib/shadcn/sonner'
import { zar, num } from '../components/cash/cashModel'
import { StatementUpload, type UploadOutcome } from '../components/cash/StatementUpload'

type Row = Record<string, any>

function monthLabel(v: unknown): string {
  return new Date(String(v)).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })
}

export default function BankStatements() {
  const { loading: accessLoading, canView, canViewFinance, caption } = useCapabilities()
  const access = useGetStatementAccess()
  const library = useGetStatementLibrary()
  const gaps = useGetStatementGaps()
  const accounts = useGetBankAccounts()
  const upload = useUploadBankStatementPdf()
  const remove = useRemoveStatementUpload()
  const docUrl = useGetStatementDocumentUrl()

  const [account, setAccount] = useState('')
  const [tab, setTab] = useState('gaps')
  const [uploading, setUploading] = useState(false)
  const [outcome, setOutcome] = useState<UploadOutcome | null>(null)

  const loadAll = useCallback(() => {
    access.trigger({}, { skipCache: true })
    accounts.trigger({}, { skipCache: true })
    library.trigger({ account_key: account || undefined }, { skipCache: true })
    gaps.trigger({ account_key: account || undefined }, { skipCache: true })
    // The hook objects are recreated each render; depending on them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account])

  useEffect(() => {
    if (canViewFinance) loadAll()
  }, [canViewFinance, loadAll])

  const accessRow = (access.data as Row | null) ?? null
  const mayManage = accessRow?.may_manage === true
  const accessReason = accessRow?.reason ?? null
  // 'unavailable' is not a refusal and must never be rendered as one. See the
  // handler: the first version of this page hid the upload with no explanation
  // when the permission lookup threw, which is "could not ask" shown as "no".
  const accessUnknown = accessRow?.state === 'unavailable' || (access.error != null && !mayManage)
  const accountRows = (accounts.data as Row[] | undefined) ?? []
  const gapRows = (gaps.data as Row[] | undefined) ?? []
  const libraryRows = (library.data as Row[] | undefined) ?? []

  const missing = useMemo(() => gapRows.filter((g) => g.missing === true), [gapRows])
  const missingWithMoney = useMemo(
    () => missing.filter((g) => Number(g.declared_deposits ?? 0) > 0),
    [missing],
  )

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView || !canViewFinance) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Fleetly&rsquo;s own bank statements are finance access. Whether one driver&rsquo;s
            deposit cleared is on the Driver Cash screen and is open to you.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Landmark className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Bank Statements</h1>
          <NativeSelect
            className="ml-auto w-64"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            aria-label="Account"
          >
            <option value="">Every account</option>
            {accountRows.map((a) => (
              <option key={String(a.account_key)} value={String(a.account_key)}>
                {a.display_name}
              </option>
            ))}
          </NativeSelect>
          {mayManage && (
            <Button
              variant="brand"
              onClick={() => {
                setOutcome(null)
                setUploading(true)
              }}
            >
              <Upload className="mr-2 h-4 w-4" /> Load a statement
            </Button>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      </div>

      {!mayManage && (accessReason || accessUnknown) && (
        <div
          className={
            accessUnknown
              ? 'flex items-start gap-2 border-b border-amber-400/40 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200'
              : 'flex items-start gap-2 border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground'
          }
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {accessReason ??
              'Whether you may load a statement could not be determined just now, so the upload is hidden rather than offered and then refused.'}
            {accessUnknown && (
              <>
                {' '}
                <button type="button" className="underline" onClick={() => loadAll()}>
                  Try again
                </button>
              </>
            )}
          </span>
        </div>
      )}

      {missing.length > 0 && (
        <div className="flex items-start gap-2 border-b border-amber-400/40 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {num(missing.length)} month{missing.length === 1 ? '' : 's'} have no statement loaded
            {missingWithMoney.length > 0 && (
              <>
                , and {num(missingWithMoney.length)} of them carry declared deposits waiting to be
                checked
              </>
            )}
            . Until a month is loaded, every deposit in it reads &ldquo;no statement loaded&rdquo;,
            which is a gap in Fleetly&rsquo;s records and not a finding about any driver.
          </span>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="sticky top-0 z-10 border-b border-border bg-card px-3 py-2">
            <TabsList>
              <TabsTrigger value="gaps">Month by month</TabsTrigger>
              <TabsTrigger value="library">Statements held</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="gaps" className="p-3">
            {mayManage && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="brand"
                  onClick={() => {
                    setOutcome(null)
                    setUploading(true)
                  }}
                >
                  <Upload className="mr-2 h-4 w-4" /> Load a bank statement
                </Button>
                <span className="text-xs text-muted-foreground">
                  Load the months carrying declared deposits first.
                </span>
              </div>
            )}
            {gaps.loading ? (
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Loading…
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Month</th>
                      <th className="px-3 py-2 text-left font-medium">Account</th>
                      <th className="px-3 py-2 text-left font-medium">Statement</th>
                      <th className="px-3 py-2 text-right font-medium">Lines</th>
                      <th className="px-3 py-2 text-left font-medium">Days covered</th>
                      <th className="px-3 py-2 text-right font-medium">Deposits declared</th>
                      <th className="px-3 py-2 text-right font-medium">Waiting to be checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gapRows.map((g) => (
                      <tr
                        key={`${g.account_key}-${g.month}`}
                        className="border-t border-border"
                      >
                        <td className="whitespace-nowrap px-3 py-1.5">{monthLabel(g.month)}</td>
                        <td className="px-3 py-1.5">{g.account_name}</td>
                        <td className="px-3 py-1.5">
                          <Chip tone={g.missing ? 'amber' : 'green'}>
                            {g.missing ? 'not loaded' : 'loaded'}
                          </Chip>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(g.lines)}</td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">
                          {g.first_day ? `${g.first_day} to ${g.last_day}` : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {num(g.declared_deposits)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {zar(g.declared_deposits_zar)}
                        </td>
                      </tr>
                    ))}
                    {gapRows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                          No Fleetly bank account is registered, so there is nothing to load a
                          statement against.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              The months run from Fleetly&rsquo;s first recorded shift, not from when the account was
              opened, because that is when the company started producing cash to account for. Load
              the months carrying declared deposits first.
            </p>
          </TabsContent>

          <TabsContent value="library" className="p-3">
            {/*
              The same action as the one in the header. It is here as well because
              this is the tab a person is looking at when they realise a statement
              is missing, and a control that lives only in a header is a control
              people ask for again.
            */}
            {mayManage && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Button
                  variant="brand"
                  onClick={() => {
                    setOutcome(null)
                    setUploading(true)
                  }}
                >
                  <Upload className="mr-2 h-4 w-4" /> Load a bank statement
                </Button>
                <span className="text-xs text-muted-foreground">
                  The PDF as the bank sends it. It is checked against its own opening balance,
                  closing balance and printed turnover before a line is loaded.
                </span>
              </div>
            )}
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Period</th>
                    <th className="px-3 py-2 text-left font-medium">Account</th>
                    <th className="px-3 py-2 text-left font-medium">Source</th>
                    <th className="px-3 py-2 text-right font-medium">Lines</th>
                    <th className="px-3 py-2 text-left font-medium">Reading</th>
                    <th className="px-3 py-2 text-left font-medium">Loaded by</th>
                    <th className="px-3 py-2 text-left font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {libraryRows.map((u) => (
                    <tr key={String(u.upload_id)} className="border-t border-border align-top">
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {u.period_from ? `${u.period_from} to ${u.period_to}` : 'not stated'}
                      </td>
                      <td className="px-3 py-1.5">{u.account_name}</td>
                      <td className="px-3 py-1.5">{u.source}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(u.lines_held)}</td>
                      <td className="px-3 py-1.5">
                        <Chip
                          tone={
                            u.reconciled === true ? 'green' : u.reconciled === false ? 'red' : 'neutral'
                          }
                        >
                          {u.reconciled === true
                            ? 'ties to the document'
                            : u.reconciled === false
                              ? 'does not tie'
                              : 'not checked'}
                        </Chip>
                        <div className="mt-1 max-w-md text-[11px] text-muted-foreground">
                          {u.reconciliation_note}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {u.uploaded_by_name || '—'}
                        <div className="text-[11px] text-muted-foreground">
                          {new Date(String(u.uploaded_at)).toLocaleDateString('en-ZA')}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex flex-col gap-1">
                          {mayManage && u.storage_path && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                const res = (await docUrl.trigger(
                                  { upload_id: u.upload_id },
                                  { skipCache: true },
                                ).result) as Row
                                if (res?.url) window.open(String(res.url), '_blank', 'noopener')
                                else toast.error('That document could not be opened.')
                              }}
                            >
                              <FileText className="mr-1 h-3.5 w-3.5" /> Open
                            </Button>
                          )}
                          {mayManage && !u.removed_at && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                const reason = window.prompt(
                                  'Why is this statement being removed? Whoever reads the account next has to know whether the evidence was wrong or the account was.',
                                )
                                if (!reason) return
                                const res = (await remove.trigger(
                                  { upload_id: u.upload_id, reason },
                                  { skipCache: true },
                                ).result) as Row
                                if (res?.ok) {
                                  toast.success(`Removed. ${res.lines_removed} lines taken back out.`)
                                  loadAll()
                                } else {
                                  toast.error('Not removed', { description: String(res?.error ?? '') })
                                }
                              }}
                            >
                              <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {libraryRows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                        No statement has ever been loaded. Until one is, no driver&rsquo;s deposit
                        can be confirmed as having arrived, and the Deposits tab on Driver Cash is
                        right to say so.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {libraryRows.some((u) => u.reconciled === false) && (
              <p className="mt-2 flex items-start gap-2 text-xs text-destructive">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                A statement whose reading does not tie is on file. Every figure taken off it is
                unproved, and it should be removed and loaded again rather than worked around.
              </p>
            )}
            {libraryRows.length > 0 && libraryRows.every((u) => u.reconciled === true) && (
              <p className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                Every statement on file was read and tied to its own opening balance, closing
                balance and printed turnover.
              </p>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {uploading && (
        <StatementUpload
          accounts={accountRows.map((a) => ({
            account_key: String(a.account_key),
            display_name: String(a.display_name),
          }))}
          busy={upload.loading}
          outcome={outcome}
          error={upload.error ?? null}
          onCancel={() => {
            setUploading(false)
            setOutcome(null)
          }}
          onSubmit={async ({ accountKey, file, contentBase64 }) => {
            const res = (await upload.trigger(
              {
                account_key: accountKey,
                filename: file.name,
                contentType: file.type || 'application/pdf',
                contentBase64,
              },
              { skipCache: true },
            ).result) as UploadOutcome
            setOutcome(res)
            if (res?.loaded) loadAll()
          }}
        />
      )}
    </div>
  )
}
